<section class="sav-blog-category-hero">
  <div class="sav-site-container">
    <app-breadcrumb [items]="crumbs()" />
    <div class="sav-blog-category-hero-row">
      <div class="sav-blog-category-hero-copy">
        <!-- Only this category's own content, never the built-in placeholder copy: until the
             category's data arrives (a few hundred ms after an in-app navigation) a neutral
             skeleton holds the space. A server render always has the data, so it never shows one. -->
        @if (contentReady()) {
          <div class="mb-[10px]">
            <span
              class="inline-flex items-center gap-3 rounded-full bg-[color-mix(in_srgb,var(--color-primary)_12%,white)] px-[14px] py-[4px] text-[12px] font-bold leading-none uppercase tracking-[0.18em] text-[var(--color-primary)]"
            >
              <span class="inline-flex">
                <span
                  class="sav-blog-category-detail-badge-icon"
                  [innerHTML]="category().iconHtml"
                ></span>
              </span>
              <span>{{ category().badgeLabel }}</span>
            </span>
          </div>

          <h1>
            {{ heroHeading() }}
          </h1>

          <p class="sav-blog-category-hero-description">
            {{ heroDescription() }}
          </p>
        } @else {
          <div class="sav-skel-hero" aria-busy="true" aria-label="Loading category">
            <span class="sav-skel sav-skel-badge"></span>
            <span class="sav-skel sav-skel-h1"></span>
            <span class="sav-skel sav-skel-line"></span>
            <span class="sav-skel sav-skel-line sav-skel-line-short"></span>
          </div>
        }
      </div>

      <!-- The site's standard blog card, the same one the listing below and every other blog page
           uses. It replaces three bespoke tiles that put the title in white over a slate-950
           gradient — a dark treatment that appeared nowhere else on the site, and which hid the
           excerpt, author and read time the shared card carries. -->
      @if (postsReady() && featuredPosts().length > 0) {
        <div class="sav-blog-category-featured">
          @for (post of featuredPosts(); track post.id) {
            <app-blog-post-card
              [post]="{
                slug: post.slug,
                title: post.title,
                excerpt: post.excerpt,
                imageUrl: post.imageUrl,
                category: post.category,
                readTime: post.readTime,
                author: post.author,
                authorPath: post.authorPath,
                storeLogoUrl: post.storeLogoUrl,
              }"
            />
          }
        </div>
      }
    </div>
  </div>
</section>

@if (contentReady() && category().topStores.length > 0) {
  <section class="sav-top-stores-section" [attr.aria-label]="category().name + ' top stores'">
    <div class="sav-top-stores-wrap">
      <h2>{{ category().name }} Top Stores</h2>

      <div class="sav-top-store-list" appAutoScrollRow>
        @for (store of category().topStores; track store.slug) {
          <a [routerLink]="['/', store.slug]" class="sav-top-store" [attr.aria-label]="store.name">
            <img [src]="store.logoUrl" [alt]="store.name" class="sav-top-store-logo" />
          </a>
        }
      </div>
    </div>
  </section>
}

<section class="px-0 py-[50px]">
  <div class="sav-site-container">
    <div class="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div>
        <!-- Shared blog card + grid: 3 per row, because the sidebar takes the rest of the row. -->
        @if (postsReady()) {
          @if (categoryPosts().length > 0) {
            <div class="sav-blog-grid has-sidebar">
              @for (post of pagedCategoryPosts(); track post.id) {
                <app-blog-post-card
                  [post]="{
                    slug: post.slug,
                    title: post.title,
                    excerpt: post.excerpt,
                    imageUrl: post.imageUrl,
                    category: post.category,
                    readTime: post.readTime,
                    author: post.author,
                    authorPath: post.authorPath,
                    storeLogoUrl: post.storeLogoUrl,
                  }"
                />
              }
            </div>

            <app-blog-pagination
              [state]="{ page: currentPage(), totalPages: totalPages() }"
              (pageChange)="onPageChange($event)"
            />
          } @else {
            <p class="sav-blog-category-empty">No guides in this category yet.</p>
          }
        } @else {
          <div class="sav-blog-grid has-sidebar" aria-busy="true" aria-label="Loading guides">
            @for (slot of [1, 2, 3]; track slot) {
              <span class="sav-skel sav-skel-card"></span>
            }
          </div>
        }
      </div>

      <aside class="sav-blog-category-sidebar space-y-6 self-start">
        @if (contentReady()) {
        @if (pageAuthor(); as author) {
          <article class="sav-side-card sav-side-card-updated">
            <span class="sav-side-card-eyebrow">Reviewed by</span>
            <div class="sav-author-row">
              @if (author.avatarUrl) {
                <img
                  class="sav-author-avatar"
                  [src]="author.avatarUrl"
                  [alt]="author.name"
                  width="96"
                  height="96"
                />
              } @else {
                <span class="sav-author-avatar sav-author-avatar-initials" aria-hidden="true">{{
                  author.initials
                }}</span>
              }
              <div class="sav-author-meta">
                <h3 class="sav-author-name">{{ author.name }}</h3>
                @if (author.role) {
                  <p class="sav-author-role">{{ author.role }}</p>
                }
              </div>
            </div>
            @if (author.bio) {
              <p>{{ author.bio }}</p>
            }
            @if (author.profileUrl) {
              <a [routerLink]="author.profileUrl"
                >See Bio
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"
                  ></path>
                </svg>
              </a>
            }
          </article>
        }

        @if (displayedExperts().length > 0) {
        <article class="sav-side-card sav-side-card-spotlight">
          <h3>Expert Spotlight</h3>

          <div class="sav-spotlight-list">
            @for (member of displayedExperts(); track member.name) {
              <div class="sav-spotlight-member">
                @if (member.avatarUrl) {
                  <img
                    class="sav-spotlight-avatar"
                    [src]="member.avatarUrl"
                    [alt]="member.name"
                    width="72"
                    height="72"
                  />
                } @else {
                  <span
                    class="sav-spotlight-avatar sav-author-avatar-initials"
                    aria-hidden="true"
                    >{{ member.initials }}</span
                  >
                }
                <div class="sav-spotlight-meta">
                  <h4 class="sav-spotlight-name">{{ member.name }}</h4>
                  @if (member.role) {
                    <p class="sav-spotlight-role">{{ member.role }}</p>
                  }
                </div>
              </div>
            }
          </div>

          <a routerLink="/experts"
            >Meet Our Entire Team
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"
              ></path>
            </svg>
          </a>
        </article>
        }
        }

        <!-- `.sav-side-card`, like the two cards above it. It was carrying its own Tailwind shell
             (slate border, 2xl radius, 30/40px padding, drop shadow), so the third card in the same
             column sat on a different border colour, radius and inset from its neighbours. -->
        <article class="sav-side-card sav-categories-card">
          <h3>Categories</h3>
          <div class="sav-categories-list">
            @for (item of categoryWidgets(); track item.slug) {
              <a [routerLink]="['/blogs', item.slug]">
                <span>{{ item.name }}</span>
                <span class="sav-categories-count">{{ item.totalBlogs }}</span>
              </a>
            }
          </div>
        </article>
      </aside>
    </div>
  </div>
</section>

@if (contentReady() && categoryProducts().length > 0) {
  <section class="sav-blog-category-products" aria-label="Products">
    <div class="sav-site-container">
      <h2 class="sav-blog-category-products-title">{{ category().name }} Products</h2>
      <app-product-grid [products]="categoryProducts()" />
    </div>
  </section>
}

@if (contentReady() && longContentHtml()) {
  <section class="sav-blog-category-longform" aria-label="Category guide">
    <div class="sav-site-container">
      <app-longform-content [html]="longContentHtml()" />
    </div>
  </section>
}

@if (contentReady()) {
<section class="sav-faq-section" aria-label="Frequently Asked Questions">
  <div class="sav-faq-wrap">
    <h2>Frequently Asked Questions</h2>
    <div class="sav-faq-list">
      @for (faq of displayedFaqs(); track $index) {
        <div class="sav-faq-item" [class.expanded]="expandedFaqIndex() === $index">
          <button
            class="sav-faq-question"
            type="button"
            [attr.aria-expanded]="expandedFaqIndex() === $index"
            (click)="toggleFaq($index)"
          >
            <span>{{ faq.question }}</span>
            <svg
              class="sav-faq-chevron"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          <div class="sav-faq-answer" [class.visible]="expandedFaqIndex() === $index">
            <div class="sav-faq-answer-body" [innerHTML]="faq.answer | trustedCmsHtml"></div>
          </div>
        </div>
      }
    </div>
  </div>
</section>
}
